/**
 * /earthworks/jobs/new — open a job (POST /earthworks/jobs). A full page, not
 * a modal. On save the job's own page replaces this one, so Back lands on the
 * register. `?customerId=&siteId=` pre-fill it (a site's "New job").
 *
 * Dispatch roles only (admin, office staff, supervisor, dispatcher); any other
 * role is told so here rather than handed a form the API would refuse.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateEwJob, useEwCustomers, useEwMeta, useEwUsers } from '../../hooks/useEarthworks';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchEw, ewRefusal } from '../../lib/earthworks';
import { useBackToList } from '../downhole/recordPage';
import { EwJobForm } from './JobForm';
import { EW_EYEBROW, EwEmpty, EwLoading, EwPanel } from './EwBits';

export default function EwNewJobScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const back = useBackToList('/earthworks/jobs');
  const { user } = useAuth();
  const canAdd = canDispatchEw(user?.role);
  const meta = useEwMeta();
  const customers = useEwCustomers(canAdd);
  const users = useEwUsers(canAdd);
  const create = useCreateEwJob();
  const [error, setError] = useState<string | null>(null);
  const metaFailure = queryLoadFailure(meta);
  const customersFailure = queryLoadFailure(customers);
  const prefill = { customerId: searchParams.get('customerId') ?? undefined, siteId: searchParams.get('siteId') ?? undefined };

  return (
    <div data-testid="ew-new-job-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={EW_EYEBROW} title="New job" description="Draft it now, or schedule it for the crew." onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <EwPanel eyebrow="Job register" title="Job">
            {!canAdd ? (
              <EwEmpty>Only an admin, office staff, a supervisor or a dispatcher can open a job.</EwEmpty>
            ) : meta.isLoading || customers.isLoading ? (
              <EwLoading label="Loading form" />
            ) : metaFailure || customersFailure ? (
              <LoadFailureNotice
                error={queryFailureError(metaFailure ? meta : customers)}
                kind={(metaFailure ?? customersFailure) || 'unreachable'}
                subject={metaFailure ? 'the job form' : 'your customers'}
              />
            ) : (customers.data ?? []).length === 0 ? (
              <EwEmpty>A job belongs to a customer, and there are no customers yet. Add one under Customers first.</EwEmpty>
            ) : (
              <EwJobForm
                job={null}
                prefill={prefill}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                customers={customers.data ?? []}
                users={users.data ?? []}
                pending={create.isPending}
                error={error}
                submitLabel="Create job"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success(res.data?.jobNumber ? `Job ${res.data.jobNumber} created` : 'Job created');
                      navigate(res.data?.id ? `/earthworks/jobs/${res.data.id}` : '/earthworks/jobs', { replace: true });
                    },
                    onError: (err) => setError(ewRefusal(err, 'The job could not be created.')),
                  });
                }}
              />
            )}
          </EwPanel>
        </div>
      </section>
    </div>
  );
}
